import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { usePaypalSnapshots } from '../../modules/audit/hooks';
import { PaypalEnvironmentSnapshot } from '../../modules/audit/models';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import {
  CreditCard,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Camera,
  GitCompare,
  Shield,
  Clock,
  TrendingUp,
  Eye,
  Activity,
  RotateCcw,
  X,
  Diff,
} from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────────────────

interface PaypalBilling {
  id: string;
  account_owner_id: string;
  subscription_id: string;
  status: string;
  plan: string;
  environment: string;
  activated_at: string | null;
  created_at: string;
}

interface PaypalWebhookEvent {
  id: string;
  event_type: string;
  payload: Record<string, unknown> | null;
  received_at: string;
  processed: boolean;
  processing_error: string | null;
}

interface HealthCheck {
  label: string;
  passed: boolean;
  detail: string;
}

// ── Utility helpers ────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function statusBadgeClass(status: string): string {
  switch (status.toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'suspended':
      return 'bg-yellow-100 text-yellow-800';
    case 'cancelled':
    case 'canceled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function JsonBlock({ data }: { data: Record<string, unknown> | null | undefined }) {
  if (!data || Object.keys(data).length === 0) {
    return <span className="text-gray-400 text-xs italic">empty</span>;
  }
  return (
    <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function EnvironmentCard({
  label,
  snapshot,
}: {
  label: string;
  snapshot: PaypalEnvironmentSnapshot | undefined;
}) {
  const hasDrift = snapshot?.drift_detected === true;
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex-1 min-w-0">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <GitCompare className="h-4 w-4 text-gray-500" />
          <h3 className="font-semibold text-gray-800 capitalize">{label}</h3>
        </div>
        {snapshot && hasDrift && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
            <AlertTriangle className="h-3 w-3" />
            Drift detected
          </span>
        )}
        {snapshot && !hasDrift && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="h-3 w-3" />
            In sync
          </span>
        )}
      </div>

      {!snapshot ? (
        <p className="text-sm text-gray-400 italic">No snapshot captured yet.</p>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Captured {formatDate(snapshot.captured_at)}
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
              Webhook Config
            </p>
            <JsonBlock data={snapshot.webhook_config} />
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
              Subscription Plans
            </p>
            <JsonBlock data={snapshot.subscription_plans} />
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
              Pricing Config
            </p>
            <JsonBlock data={snapshot.pricing_config} />
          </div>

          {hasDrift && snapshot.drift_details && Object.keys(snapshot.drift_details).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-orange-600 mb-1 uppercase tracking-wide">
                Drift Details
              </p>
              <JsonBlock data={snapshot.drift_details} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Snapshot Modal ─────────────────────────────────────────────────────────────

interface CaptureModalProps {
  userId: string;
  onClose: () => void;
  onSaved: () => void;
}

function CaptureSnapshotModal({ userId, onClose, onSaved }: CaptureModalProps) {
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const { error: insertError } = await supabase
        .from('paypal_environment_snapshots')
        .insert({
          environment,
          description: description.trim() || null,
          webhook_config: {},
          subscription_plans: {},
          pricing_config: {},
          api_credentials_hash: 'not-captured',
          checkout_script_hash: 'not-captured',
          captured_by: userId,
          drift_detected: false,
          drift_details: {},
        });

      if (insertError) throw insertError;

      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 z-10">
        <div className="flex items-center gap-2 mb-5">
          <Camera className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Capture PayPal Snapshot</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Environment
            </label>
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as 'sandbox' | 'production')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
            >
              <option value="sandbox">Sandbox</option>
              <option value="production">Production</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Pre-deployment baseline"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm rounded-lg bg-teal-500 text-white font-medium hover:bg-teal-600 disabled:opacity-50 transition-colors flex items-center gap-1.5"
            >
              {saving ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Camera className="h-3.5 w-3.5" />
              )}
              {saving ? 'Saving…' : 'Capture'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Restore Confirmation Modal ─────────────────────────────────────────────────

interface RestoreModalProps {
  environment: 'sandbox' | 'production';
  snapshot: PaypalEnvironmentSnapshot;
  userId: string;
  onClose: () => void;
  onRestored: () => void;
}

function RestoreConfirmModal({ environment, snapshot, userId, onClose, onRestored }: RestoreModalProps) {
  const [reason, setReason] = useState('');
  const [step, setStep] = useState<'reason' | 'confirm' | 'done'>('reason');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = `RESTORE-${environment.toUpperCase()}-${snapshot.id.slice(0, 8).toUpperCase()}`;

  const handleConfirm = async () => {
    if (!reason.trim()) { setError('Reason is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const { error: insertErr } = await supabase.from('restore_events').insert({
        restore_type: `paypal_${environment}`,
        snapshot_id: snapshot.id,
        initiated_by: userId,
        reason: reason.trim(),
        confirmation_token: token,
        status: 'initiated',
      });
      if (insertErr) throw insertErr;
      await supabase.from('audit_snapshots').insert({
        snapshot_type: 'paypal_restore_audit',
        environment,
        snapshot_data: { snapshot_id: snapshot.id, reason: reason.trim(), token, restored_by: userId },
        integrity_hash: token,
        created_by: userId,
        description: `PayPal ${environment} restore initiated`,
      });
      setStep('done');
      onRestored();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6 z-10">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-red-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              Restore Last Working {environment === 'production' ? 'Production' : 'Sandbox'} State
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        {step === 'reason' && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Warning:</strong> This logs a restore event against snapshot <code className="font-mono text-xs">{snapshot.id.slice(0, 16)}...</code> captured {new Date(snapshot.captured_at).toLocaleString()}.
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason for restore <span className="text-red-500">*</span></label>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Describe why this restore is needed..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
              />
            </div>
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => { if (!reason.trim()) { setError('Reason is required.'); return; } setError(null); setStep('confirm'); }}
                className="px-4 py-2 text-sm rounded-lg bg-amber-500 text-white font-medium hover:bg-amber-600">Continue</button>
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              <strong>Final confirmation required.</strong> This action will be permanently logged in the audit ledger and cannot be undone.
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Restore token (for audit log):</p>
              <code className="block bg-gray-100 border border-gray-200 rounded px-3 py-2 text-xs font-mono break-all">{token}</code>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Reason:</p>
              <p className="text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded px-3 py-2">{reason}</p>
            </div>
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setStep('reason')} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Back</button>
              <button onClick={handleConfirm} disabled={saving}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5">
                {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                {saving ? 'Logging restore…' : 'Confirm Restore'}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800 flex items-start gap-2">
              <CheckCircle className="h-5 w-5 shrink-0 mt-0.5" />
              <div>
                <strong>Restore event logged.</strong>
                <p className="mt-1">Token: <code className="font-mono text-xs">{token}</code></p>
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-900">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Drift Diff Viewer ──────────────────────────────────────────────────────────

function DriftDiffViewer({ sandbox, live }: { sandbox: PaypalEnvironmentSnapshot | undefined; live: PaypalEnvironmentSnapshot | undefined }) {
  if (!sandbox || !live) {
    return <p className="text-sm text-gray-400 italic">Capture snapshots for both environments to compare.</p>;
  }

  const fields: Array<{ key: keyof PaypalEnvironmentSnapshot; label: string }> = [
    { key: 'webhook_config', label: 'Webhook Config' },
    { key: 'subscription_plans', label: 'Subscription Plans' },
    { key: 'pricing_config', label: 'Pricing Config' },
    { key: 'api_credentials_hash', label: 'API Credentials Hash' },
    { key: 'checkout_script_hash', label: 'Checkout Script Hash' },
  ];

  const diffs = fields.filter((f) => {
    const a = JSON.stringify(sandbox[f.key]);
    const b = JSON.stringify(live[f.key]);
    return a !== b;
  });

  if (diffs.length === 0) {
    return (
      <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm">
        <CheckCircle className="h-4 w-4" />
        No differences detected between sandbox and production snapshots.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {diffs.map((f) => (
        <div key={f.key} className="bg-white border border-orange-200 rounded-lg overflow-hidden">
          <div className="bg-orange-50 px-4 py-2 text-xs font-semibold text-orange-700 uppercase tracking-wide flex items-center gap-1.5">
            <Diff className="h-3.5 w-3.5" />
            {f.label}
          </div>
          <div className="grid grid-cols-2 divide-x divide-gray-100">
            <div className="p-3">
              <p className="text-xs font-medium text-gray-500 mb-1">Sandbox</p>
              <pre className="text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap break-all">
                {JSON.stringify(sandbox[f.key], null, 2)}
              </pre>
            </div>
            <div className="p-3">
              <p className="text-xs font-medium text-gray-500 mb-1">Production</p>
              <pre className="text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap break-all">
                {JSON.stringify(live[f.key], null, 2)}
              </pre>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function PaypalAudit() {
  const { user } = useAuth();
  const { snapshots, loading: snapshotsLoading, refetch: refetchSnapshots } = usePaypalSnapshots();

  const [billings, setBillings] = useState<PaypalBilling[]>([]);
  const [billingsLoading, setBillingsLoading] = useState(true);

  const [webhookEvents, setWebhookEvents] = useState<PaypalWebhookEvent[]>([]);
  const [webhooksLoading, setWebhooksLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<{ env: 'sandbox' | 'production'; snapshot: PaypalEnvironmentSnapshot } | null>(null);

  // ── Data fetchers ────────────────────────────────────────────────────────────

  const fetchBillings = useCallback(async () => {
    setBillingsLoading(true);
    try {
      const { data } = await supabase
        .from('paypal_billing')
        .select('id, account_owner_id, subscription_id, status, plan, environment, activated_at, created_at')
        .order('created_at', { ascending: false });
      setBillings(data ?? []);
    } catch {
      setBillings([]);
    } finally {
      setBillingsLoading(false);
    }
  }, []);

  const fetchWebhookEvents = useCallback(async () => {
    setWebhooksLoading(true);
    try {
      const { data } = await supabase
        .from('paypal_webhook_events')
        .select('id, event_type, payload, received_at, processed, processing_error')
        .order('received_at', { ascending: false })
        .limit(20);
      setWebhookEvents(data ?? []);
    } catch {
      setWebhookEvents([]);
    } finally {
      setWebhooksLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBillings();
    fetchWebhookEvents();
  }, [fetchBillings, fetchWebhookEvents]);

  // ── Derived values ───────────────────────────────────────────────────────────

  const latestSnapshot =
    snapshots.length > 0
      ? [...snapshots].sort(
          (a, b) => new Date(b.captured_at).getTime() - new Date(a.captured_at).getTime()
        )[0]
      : undefined;

  const sandboxSnapshot = snapshots
    .filter((s) => s.environment === 'sandbox')
    .sort((a, b) => new Date(b.captured_at).getTime() - new Date(a.captured_at).getTime())[0];

  const liveSnapshot = snapshots
    .filter((s) => s.environment === 'production' || s.environment === 'live')
    .sort((a, b) => new Date(b.captured_at).getTime() - new Date(a.captured_at).getTime())[0];

  // Health score calculation
  const now = Date.now();
  const twentyFourHrsAgo = now - 24 * 60 * 60 * 1000;

  const hasSnapshots = snapshots.length > 0;
  const noDriftInLatest = latestSnapshot ? !latestSnapshot.drift_detected : false;
  const webhooksIn24h = webhookEvents.some(
    (e) => new Date(e.received_at).getTime() >= twentyFourHrsAgo
  );
  const noFailedWebhooks = webhookEvents.every((e) => !e.processing_error);
  const activeCount = billings.filter((b) => b.status === 'active').length;
  const billingHealthy = billings.length === 0 || activeCount > 0;

  const healthChecks: HealthCheck[] = [
    {
      label: 'Snapshots exist',
      passed: hasSnapshots,
      detail: hasSnapshots
        ? `${snapshots.length} snapshot(s) on record`
        : 'No snapshots captured yet',
    },
    {
      label: 'No drift in latest snapshot',
      passed: noDriftInLatest,
      detail: latestSnapshot
        ? noDriftInLatest
          ? 'Latest snapshot shows no drift'
          : 'Drift was detected in the latest snapshot'
        : 'No snapshot to evaluate',
    },
    {
      label: 'Webhooks received in last 24h',
      passed: webhooksIn24h,
      detail: webhooksIn24h
        ? 'Recent webhook activity detected'
        : 'No webhooks received in the last 24 hours',
    },
    {
      label: 'No failed webhook events',
      passed: noFailedWebhooks,
      detail: noFailedWebhooks
        ? 'All recent webhooks processed cleanly'
        : 'Some webhook events have processing errors',
    },
    {
      label: 'Active subscriptions in billing table',
      passed: billingHealthy,
      detail:
        billings.length === 0
          ? 'No billing records found'
          : `${activeCount} active subscription(s)`,
    },
  ];

  const healthScore = healthChecks.filter((c) => c.passed).length * 20;

  const healthColor =
    healthScore >= 80
      ? 'text-green-600'
      : healthScore >= 50
      ? 'text-yellow-500'
      : 'text-red-500';

  const healthBg =
    healthScore >= 80
      ? 'bg-green-50 border-green-200'
      : healthScore >= 50
      ? 'bg-yellow-50 border-yellow-200'
      : 'bg-red-50 border-red-200';

  // Subscription status breakdown
  const statusCounts = billings.reduce<Record<string, number>>((acc, b) => {
    acc[b.status] = (acc[b.status] ?? 0) + 1;
    return acc;
  }, {});

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleSnapshotSaved = () => {
    refetchSnapshots();
  };

  const handleRefreshAll = () => {
    refetchSnapshots();
    fetchBillings();
    fetchWebhookEvents();
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-8">

        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Shield className="h-6 w-6 text-teal-500" />
              <h1 className="text-2xl font-bold text-gray-900">PayPal Audit Dashboard</h1>
            </div>
            {latestSnapshot ? (
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                Last snapshot: {formatDate(latestSnapshot.captured_at)}
              </p>
            ) : (
              <p className="text-sm text-gray-400 italic">No snapshots captured yet</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleRefreshAll}
              className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-teal-500 text-white font-medium hover:bg-teal-600 transition-colors shadow-sm"
            >
              <Camera className="h-4 w-4" />
              Capture Snapshot
            </button>
          </div>
        </div>

        {/* ── Health Score Card ── */}
        <div className={`rounded-2xl border ${healthBg} p-6`}>
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            {/* Score */}
            <div className="flex flex-col items-center justify-center min-w-[120px]">
              <div className={`text-6xl font-black tabular-nums ${healthColor}`}>
                {healthScore}
              </div>
              <div className="text-sm font-medium text-gray-500 mt-1">/ 100</div>
              <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wide font-semibold">
                Health Score
              </div>
            </div>

            {/* Divider */}
            <div className="hidden md:block w-px bg-gray-200 self-stretch" />

            {/* Check list */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {healthChecks.map((check) => (
                <div key={check.label} className="flex items-start gap-2">
                  {check.passed ? (
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-800">{check.label}</p>
                    <p className="text-xs text-gray-500">{check.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Environment Comparison ── */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <GitCompare className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Environment Comparison</h2>
          </div>

          {snapshotsLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Loading snapshots…
            </div>
          ) : (
            <div className="flex flex-col md:flex-row gap-4">
              <EnvironmentCard label="Sandbox" snapshot={sandboxSnapshot} />
              <EnvironmentCard label="Production / Live" snapshot={liveSnapshot} />
            </div>
          )}
        </section>

        {/* ── Restore Actions ── */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <RotateCcw className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">One-Click Restore</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Restore Last Working Production State</h3>
                  {liveSnapshot ? (
                    <p className="text-xs text-gray-500">Snapshot from {new Date(liveSnapshot.captured_at).toLocaleString()}</p>
                  ) : (
                    <p className="text-xs text-gray-400 italic">No production snapshot available</p>
                  )}
                </div>
                <button
                  onClick={() => liveSnapshot && setRestoreTarget({ env: 'production', snapshot: liveSnapshot })}
                  disabled={!liveSnapshot}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Restore
                </button>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Restore Last Working Sandbox State</h3>
                  {sandboxSnapshot ? (
                    <p className="text-xs text-gray-500">Snapshot from {new Date(sandboxSnapshot.captured_at).toLocaleString()}</p>
                  ) : (
                    <p className="text-xs text-gray-400 italic">No sandbox snapshot available</p>
                  )}
                </div>
                <button
                  onClick={() => sandboxSnapshot && setRestoreTarget({ env: 'sandbox', snapshot: sandboxSnapshot })}
                  disabled={!sandboxSnapshot}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Restore
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ── Active Drift Detection ── */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Diff className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Sandbox vs Production Diff</h2>
          </div>
          {snapshotsLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
              <RefreshCw className="h-4 w-4 animate-spin" /> Comparing environments…
            </div>
          ) : (
            <DriftDiffViewer sandbox={sandboxSnapshot} live={liveSnapshot} />
          )}
        </section>

        {/* ── Webhook Activity ── */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Activity className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Webhook Activity</h2>
            <span className="text-xs text-gray-400 ml-1">(last 20 events)</span>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {webhooksLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 p-6">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading webhook events…
              </div>
            ) : webhookEvents.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Eye className="h-8 w-8 mb-2 opacity-40" />
                <p className="text-sm">No webhook events found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                        Event Type
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                        Received At
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">
                        Error
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {webhookEvents.map((evt) => (
                      <tr key={evt.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-mono text-xs text-gray-800 whitespace-nowrap">
                          {evt.event_type}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {formatDate(evt.received_at)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {evt.processed ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <CheckCircle className="h-3 w-3" />
                              Processed
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <AlertTriangle className="h-3 w-3" />
                              Unprocessed
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-red-600 text-xs max-w-xs truncate">
                          {evt.processing_error ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* ── Subscription Health ── */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Subscription Health</h2>
          </div>

          {/* Status summary pills */}
          {!billingsLoading && billings.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(statusCounts).map(([status, count]) => (
                <span
                  key={status}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${statusBadgeClass(status)}`}
                >
                  <TrendingUp className="h-3 w-3" />
                  {count} {status}
                </span>
              ))}
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {billingsLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 p-6">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading subscriptions…
              </div>
            ) : billings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <CreditCard className="h-8 w-8 mb-2 opacity-40" />
                <p className="text-sm">No billing records found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                        Subscription ID
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                        Plan
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                        Environment
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                        Activated At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {billings.map((b) => (
                      <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">
                          {b.subscription_id || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-800 capitalize whitespace-nowrap">
                          {b.plan || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600 capitalize whitespace-nowrap">
                          {b.environment || '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(b.status)}`}
                          >
                            {b.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {formatDate(b.activated_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* ── Capture Snapshot Modal ── */}
      {showModal && user && (
        <CaptureSnapshotModal
          userId={user.id}
          onClose={() => setShowModal(false)}
          onSaved={handleSnapshotSaved}
        />
      )}

      {/* ── Restore Confirm Modal ── */}
      {restoreTarget && user && (
        <RestoreConfirmModal
          environment={restoreTarget.env}
          snapshot={restoreTarget.snapshot}
          userId={user.id}
          onClose={() => setRestoreTarget(null)}
          onRestored={() => { refetchSnapshots(); setRestoreTarget(null); }}
        />
      )}
    </AdminLayout>
  );
}
